import { useSession } from "next-auth/react";
import { useEffect, useRef } from "react";

import type { RouterOutputs } from "@calcom/trpc/react";
import { trpc } from "@calcom/trpc/react";

import usePublicPage from "@lib/hooks/usePublicPage";

type MeUser = RouterOutputs["viewer"]["me"];
type ProFeatureAttributes = NonNullable<MeUser["proFeatures"]>;

type UserGuidingAttributes = {
  name?: string;
  email?: string;
  role?: string;
  created_at?: number;
  isDemo?: boolean;
  company?: {
    id: string;
    name?: string;
  };
} & Partial<ProFeatureAttributes>;

type UserGuidingApi = {
  identify: (userId: string, attributes?: UserGuidingAttributes) => void;
  clearIdentity?: () => void;
};

declare global {
  interface Window {
    userGuiding?: UserGuidingApi;
    userGuidingLayer?: Array<{ event: string; fn: () => void }>;
  }
}

function buildAttributesFromMe(user: MeUser): UserGuidingAttributes {
  const attributes: UserGuidingAttributes = { ...user.proFeatures };

  if (user.name) attributes.name = user.name;
  if (user.email) attributes.email = user.email;
  if (user.role) attributes.role = user.role;
  if (user.createdDate) attributes.created_at = new Date(user.createdDate).getTime();
  if (typeof user.isDemo === "boolean") attributes.isDemo = user.isDemo;

  if (user.selectedGroup?.id != null) {
    attributes.company = {
      id: String(user.selectedGroup.id),
      ...(user.selectedGroup.name ? { name: user.selectedGroup.name } : {}),
    };
  }

  return attributes;
}

function runWhenUserGuidingReady(fn: () => void) {
  if (typeof window === "undefined") return;

  if (typeof window.userGuiding?.identify === "function") {
    fn();
    return;
  }

  window.userGuidingLayer = window.userGuidingLayer || [];
  window.userGuidingLayer.push({
    event: "onload",
    fn,
  });
}

function identifyUser(userId: string, attributes: UserGuidingAttributes) {
  runWhenUserGuidingReady(() => {
    window.userGuiding?.identify(userId, attributes);
  });
}

/**
 * Identifies the authenticated user with UserGuiding so guides/surveys persist across devices.
 * Must run after the UserGuiding container script in `_document.tsx`.
 */
export default function UserGuidingIdentify() {
  const isPublicPage = usePublicPage();
  const { data: session, status } = useSession();
  const { data: me } = trpc.viewer.me.useQuery(undefined, {
    enabled: !isPublicPage && status === "authenticated",
    retry: false,
  });
  const identifiedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (isPublicPage) return;

    if (status !== "authenticated" || !session?.user?.id) {
      if (identifiedUserIdRef.current) {
        runWhenUserGuidingReady(() => {
          window.userGuiding?.clearIdentity?.();
        });
        identifiedUserIdRef.current = null;
      }
      return;
    }

    const userId = String(session.user.id);
    const attributes: UserGuidingAttributes = me
      ? buildAttributesFromMe(me)
      : {
          ...(session.user.name ? { name: session.user.name } : {}),
          ...(session.user.email ? { email: session.user.email } : {}),
          ...(session.user.role ? { role: session.user.role } : {}),
        };

    identifyUser(userId, attributes);
    identifiedUserIdRef.current = userId;
  }, [
    isPublicPage,
    status,
    session?.user?.id,
    session?.user?.name,
    session?.user?.email,
    session?.user?.role,
    me,
  ]);

  return null;
}
